import queryString from 'query-string';
import { CommonResponse } from 'src/model/common';
import {
  CheckEmailValidation,
  CompanyAddresses,
  CompanyContacts,
  CompanyContactsByAI,
  CompanyDetail,
  CompanyIndustry,
  CompanyMarketPlaces,
  CompanyNames,
  CompanyProfileQuickView,
  CompanyProfiles,
  CompanyReviewDetail,
  CompanyReviews,
  CompanySocialMedia,
  CompanySocialMediaAI,
  CompanySocialReviews,
  CompanyType,
  CompanyWebDetail,
  CompareCompanies,
  Jurisdictions,
  NegativeMediaList,
  RelatedCompanies,
  RelatedCorporations,
  RelatedQuestions,
  SanctionCompanies,
} from 'src/model/company';
import { getBlobRequest, getRequest, postRequest, postRequestBlob } from 'src/services/api/http';
import { URL_ENDPOINT } from 'src/utils/constants/service';
import { handleQueryParams } from 'src/utils/helpers/queryParam';

const CompanyService = {
  getJurisdictions,
  getCompanyTypes,
  getCompanyIndustry,
  getListCompany,
  getQuickViewCompanyProfile,
  getCompanyDetail,
  getCompanyNames,
  contributeData,
  getCompanyWebDetail,
  getCompanyReviewFromSocial,
  getCompanyReviewDetail,
  getCompanyReviews,
  getCompareCompanies,
  getCompanyMarketplace,
  getCompanyContacts,
  getCompanyContactsByAI,
  getCompanyAddress,
  getCompanySocialMedia,
  getCompanySocialMediaByAI,
  createReview,
  getListNegativeMedia,
  getListExactNegativeMedia,
  checkEmailValidation,
  getListRelatedCorporations,
  getSanctionsCompanies,
  exportCompanyCertification,
  getRelatedQuestions,
  checkCompanyTabIsExist,
  getListRelatedCompanies,
  printCompanyDetail,
};

export default CompanyService;

function getJurisdictions(countryCode?: string[]) {
  const params = handleQueryParams('country_code', countryCode);
  const url = URL_ENDPOINT.company.commons + `jurisdictions?${params}`;
  return getRequest(url, {}, new Jurisdictions()) as Promise<Jurisdictions>;
}

function getCompanyTypes(jurisdiction: string[]) {
  const params = handleQueryParams('jurisdiction', jurisdiction);
  const url = URL_ENDPOINT.company.commons + `company_type?${params}`;
  return getRequest(url, {}, new CompanyType()) as Promise<CompanyType>;
}

function getCompanyIndustry(countryCode: string[]) {
  const params = handleQueryParams('country_code', countryCode);
  const url = URL_ENDPOINT.company.commons + `industry?${params}`;
  return getRequest(url, {}, new CompanyIndustry()) as Promise<CompanyIndustry>;
}

function getListCompany(query: string) {
  const parseQuery = queryString.parse(query);
  // const isTradeCompanyOnly = parseQuery.is_trade_company === 'true';
  const url = URL_ENDPOINT.company.companies + 'list?' + query;
  // if (isTradeCompanyOnly) url = URL_ENDPOINT.company.companies + 'list-trade-companies?' + query;
  return getRequest(url, {}, new CompanyProfiles()) as Promise<CompanyProfiles>;
}

function getQuickViewCompanyProfile(id: string) {
  const url = URL_ENDPOINT.company.companies + `quick-view/${id}`;
  return getRequest(url, {}, new CompanyProfileQuickView()) as Promise<CompanyProfileQuickView>;
}

function getCompanyNames(params: Record<string, string | number | boolean | Array<number>>, jurisdiction: string[]) {
  const paramJurisdiction = handleQueryParams('jurisdictions', jurisdiction);
  const url = URL_ENDPOINT.company.commons + `list-search-companies?${paramJurisdiction}`;
  return getRequest(url, params, new CompanyNames()) as Promise<CompanyNames>;
}

function getCompanyDetail(id: string) {
  const url = URL_ENDPOINT.company.companies + 'detail/ai/' + id;
  return getRequest(url, {}, new CompanyDetail()) as Promise<CompanyDetail>;
}

function contributeData(body: TypeDTOContributeCompany | TypeDTOReportWrong | TypeDTOMergeProfile) {
  const url = URL_ENDPOINT.company.companies + 'contribute';
  return postRequest(url, body) as Promise<CommonResponse>;
}

function getCompanyWebDetail(id: string) {
  const url = URL_ENDPOINT.company.companies + 'list-web-detail/' + id;
  return getRequest(url, {}, new CompanyWebDetail()) as Promise<CompanyWebDetail>;
}

function getCompanyReviewFromSocial(id: string) {
  const url = URL_ENDPOINT.company.companies + `social-review/${id}`;
  return getRequest(url, {}, new CompanySocialReviews()) as Promise<CompanySocialReviews>;
}

function getCompanyReviewDetail(id: string) {
  const url = URL_ENDPOINT.company.companies + `review/${id}/detail`;
  return getRequest(url, {}, new CompanyReviewDetail()) as Promise<CompanyReviewDetail>;
}

function getCompanyReviews(id: string, query: Record<string, string | number>) {
  const url = URL_ENDPOINT.company.companies + `review/${id}/reviews`;
  return getRequest(url, query, new CompanyReviews()) as Promise<CompanyReviews>;
}

function getCompareCompanies(companyIds: string[]) {
  const url = `${URL_ENDPOINT.company.companies}compare-companies?${queryString.stringify({ company_ids: companyIds }, { arrayFormat: 'none' })}`;
  return getRequest(url, {}, new CompareCompanies()) as Promise<CompareCompanies>;
}

function getCompanyMarketplace(id: string, query: Record<string, string | number>) {
  const url = URL_ENDPOINT.company.companies + `market-price/${id}`;
  return getRequest(url, query, new CompanyMarketPlaces()) as Promise<CompanyMarketPlaces>;
}

function getCompanyContacts({ id, refer_from }: { id: string; refer_from: string }) {
  const url = URL_ENDPOINT.company.companies + `contacts/${id}`;
  return getRequest(
    url,
    {
      ...(refer_from && {
        refer_from: refer_from,
      }),
    },
    new CompanyContacts(),
  ) as Promise<CompanyContacts>;
}

function getCompanyContactsByAI({ id }: { id: string }) {
  const url = URL_ENDPOINT.company.companies + `contacts/ai/${id}`;
  return getRequest(url, {}, new CompanyContactsByAI()) as Promise<CompanyContactsByAI>;
}

function getCompanyAddress(id: string) {
  const url = URL_ENDPOINT.company.companies + `get-lat-lng-by-company-id/${id}`;
  return getRequest(url, {}, new CompanyAddresses()) as Promise<CompanyAddresses>;
}

function getCompanySocialMedia(id: string) {
  const url = URL_ENDPOINT.company.companies + `social-media/${id}`;
  return getRequest(url, {}, new CompanySocialMedia()) as Promise<CompanySocialMedia>;
}

function getCompanySocialMediaByAI(id: string) {
  const url = URL_ENDPOINT.company.companies + `social-media/ai/${id}`;
  return getRequest(url, {}, new CompanySocialMediaAI()) as Promise<CompanySocialMediaAI>;
}

function createReview(body: TypeDTOReviewCompany) {
  const url = URL_ENDPOINT.company.companies + 'review';
  return postRequest(url, body) as Promise<CommonResponse>;
}

function getListNegativeMedia(params: Record<string, string | number>, id?: string) {
  const url = URL_ENDPOINT.company.companies + `negative-story-web/${id}`;
  return getRequest(url, params, new NegativeMediaList()) as Promise<NegativeMediaList>;
}

function getListExactNegativeMedia(params: Record<string, string | number>, id?: string) {
  const url = URL_ENDPOINT.company.companies + `negative-exactly-story-web/${id}`;
  return getRequest(url, params, new NegativeMediaList()) as Promise<NegativeMediaList>;
}

function checkEmailValidation({ email }: { email: string }) {
  const url = URL_ENDPOINT.company.companies + 'check-email-validation';
  return getRequest(url, { email }, new CheckEmailValidation()) as Promise<CheckEmailValidation>;
}

function getListRelatedCorporations(id: string, query: Record<string, string | number>) {
  const url = URL_ENDPOINT.company.companies + `related-companies/${id}?`;
  return getRequest(url, query, new RelatedCorporations()) as Promise<RelatedCorporations>;
}

function getSanctionsCompanies(id: string, param: Record<string, string | number>) {
  const url = URL_ENDPOINT.company.companies + `sanction/${id}?`;
  return getRequest(url, param, new SanctionCompanies()) as Promise<SanctionCompanies>;
}

function exportCompanyCertification(id: string) {
  const url = URL_ENDPOINT.company.companies + 'export-pdf/' + id;
  return getBlobRequest(
    url,
    {},
    {
      accept: 'application/json',
    },
  );
}
function printCompanyDetail(dto: Record<string, string>) {
  const url = URL_ENDPOINT.myReport.printCompanyDetail;
  return postRequestBlob(url, dto) as Promise<Blob>;
}

function getRelatedQuestions(company_id = '', query: Record<string, number | string>) {
  const url = URL_ENDPOINT.company.companies + `review/${company_id}/popular-questions`;
  return getRequest(url, query) as Promise<RelatedQuestions>;
}

function checkCompanyTabIsExist(company_id = '') {
  const url = URL_ENDPOINT.company.companies + `check-tab-exist/${company_id}`;
  return getRequest(url, {}) as Promise<CommonResponse>;
}

function getListRelatedCompanies(id: string, query: Record<string, string | number>) {
  const url = URL_ENDPOINT.company.companies + `related-companies-profile/${id}?`;
  return getRequest(url, query, new RelatedCompanies()) as Promise<RelatedCompanies>;
}
